"use client";

import { Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";

interface DescriptionStepProps {
  control: Control<any>;
}

export function DescriptionStep({ control }: DescriptionStepProps) {
  const { t } = useTranslation(["finances", "common"]);

  return (
    <div className="flex h-full flex-col justify-center px-6 py-8 max-sm:px-4 max-sm:py-6">
      {/* Header */}
      <div className="mb-8 text-center max-sm:mb-6">
        <h1 className="text-accent-foreground mb-3 flex items-center justify-center gap-3 text-2xl font-bold max-sm:text-xl">
          <Icon
            className="text-muted-foreground"
            height="1.75rem"
            icon="tabler:file-text"
            width="1.75rem"
          />
          {t("finances:createPayment.steps.addDescription")}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-md text-base max-sm:text-sm">
          {t("finances:createPayment.steps.descriptionDesc")}
        </p>
      </div>

      {/* Description Input */}
      <div className="flex justify-center">
        <div className="w-full max-w-3xl space-y-6">
          <FormField
            control={control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <div className="bg-muted/30 rounded-xl p-2">
                  <FormControl>
                    <ScrollArea className="h-[140px] w-full rounded-lg max-sm:h-[120px]">
                      <Textarea
                        className="hover:bg-muted/20 focus:bg-muted/10 min-h-[140px] resize-none rounded-lg border-none bg-transparent p-4 text-base leading-relaxed max-sm:min-h-[120px]"
                        maxLength={2000}
                        placeholder={t("finances:createPayment.descriptionPlaceholder")}
                        {...field}
                      />
                    </ScrollArea>
                  </FormControl>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t("finances:createPayment.descriptionHelper")}
                  </p>
                  <div className="text-muted-foreground text-sm">
                    {field.value?.length || 0}/2000
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Tips */}
          <div className="bg-muted/30 rounded-xl p-4">
            <h3 className="text-accent-foreground mb-3 text-sm font-semibold">
              {t("finances:createPayment.descriptionTips")}
            </h3>
            <ul className="text-muted-foreground space-y-2 text-sm">
              <li className="flex items-start gap-3">
                <Icon
                  className="text-accent-foreground mt-0.5 flex-shrink-0"
                  height="1rem"
                  icon="tabler:check"
                  width="1rem"
                />
                <span>{t("finances:createPayment.tip1")}</span>
              </li>
              <li className="flex items-start gap-3">
                <Icon
                  className="text-accent-foreground mt-0.5 flex-shrink-0"
                  height="1rem"
                  icon="tabler:check"
                  width="1rem"
                />
                <span>{t("finances:createPayment.tip2")}</span>
              </li>
              <li className="flex items-start gap-3">
                <Icon
                  className="text-accent-foreground mt-0.5 flex-shrink-0"
                  height="1rem"
                  icon="tabler:check"
                  width="1rem"
                />
                <span>{t("finances:createPayment.tip3")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
